import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { hubService, type RagDocument, type RagDocumentChunksInfo } from '../../services/hub.service';
import { errorText } from '../../lib/assistantView';
import {
  DOCUMENT_TYPES,
  deleteWarning,
  documentFields,
  documentsListQuery,
  emptyDocumentForm,
  formFromDocument,
  isAutoDocument,
  pageCount,
  sourceLabel,
  validateDocumentForm,
  type DocumentForm,
  type OriginFilter,
} from '../../lib/documentForm';
import { AiConfigModal } from '../aiconfig';
import { CyrillicNote } from '../quality/CyrillicNote';

const input = 'rounded border border-gray-300 px-3 py-2 text-sm';

/** Knowledge › Documents for one bot: list (paged, manual / auto), add and edit (text or file), sync, delete, chunks. */
export function DocumentsPanel({ system, botId }: { system: string; botId: string }) {
  const [origin, setOrigin] = useState<OriginFilter>('');
  const [documentType, setDocumentType] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<RagDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [editor, setEditor] = useState<{ id: number | null; form: DocumentForm } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [chunks, setChunks] = useState<RagDocumentChunksInfo | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await hubService.listRagDocumentsPage(documentsListQuery({ system, botId, page, origin, documentType }));
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [system, botId, page, origin, documentType]);

  useEffect(() => {
    void load();
  }, [load]);

  const [params, setParams] = useSearchParams();
  const docParam = params.get('doc');
  useEffect(() => {
    // Linked from Quality › Answers to review: open that document's editor once, then drop the param so a reload
    // or a tab switch does not reopen it. GET rag/documents/:id is not bot-scoped (pre-existing, ROADMAP), so the
    // panel itself refuses another bot's document.
    if (!docParam || !/^\d+$/.test(docParam)) return;
    void (async () => {
      try {
        const full = await hubService.getRagDocumentById(Number(docParam));
        if (String(full.botId) === String(botId) && full.system === system) setEditor({ id: full.id, form: formFromDocument(full) });
        else setError('That document is not in this bot.');
      } catch (e) {
        setError(errorText(e));
      } finally {
        const next = new URLSearchParams(params);
        next.delete('doc');
        setParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docParam]);

  function openNew() {
    setEditor({ id: null, form: emptyDocumentForm() });
    setFile(null);
    setFormError(null);
  }

  async function openEdit(d: RagDocument) {
    setFormError(null);
    setFile(null);
    try {
      // The list may not carry the full text; the edit form needs it.
      const full = await hubService.getRagDocumentById(d.id);
      setEditor({ id: d.id, form: formFromDocument(full) });
    } catch (e) {
      setError(errorText(e));
    }
  }

  function setForm(patch: Partial<DocumentForm>) {
    setEditor((ed) => (ed ? { ...ed, form: { ...ed.form, ...patch } } : ed));
  }

  async function save() {
    if (!editor) return;
    const editing = editor.id != null;
    const problem = validateDocumentForm(editor.form, { editing, file });
    if (problem) {
      setFormError(problem);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const fields = documentFields(editor.form, { editing, system, botId });
      await hubService.saveRagDocument(editor.id, fields, editor.form.contentSource === 'file' ? file : null);
      setEditor(null);
      await load();
    } catch (e) {
      setFormError(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  async function remove(d: RagDocument) {
    if (!window.confirm(deleteWarning(d))) return;
    setBusyId(d.id);
    try {
      await hubService.deleteDocument(String(d.id));
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusyId(null);
    }
  }

  async function sync(d: RagDocument) {
    setBusyId(d.id);
    try {
      await hubService.syncRagDocument(d.id);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusyId(null);
    }
  }

  async function showChunks(d: RagDocument) {
    try {
      setChunks(await hubService.getRagDocumentChunks(d.id, { limit: 50, offset: 0 }));
    } catch (e) {
      setError(errorText(e));
    }
  }

  const pages = pageCount(total);
  const form = editor?.form;

  return (
    <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-medium text-gray-900 mr-auto">Documents</h2>
        <select
          aria-label="Who made it"
          className={input}
          value={origin}
          onChange={(e) => {
            setOrigin(e.target.value as OriginFilter);
            setPage(1);
          }}
        >
          <option value="">All</option>
          <option value="manual">Manual</option>
          <option value="auto">Auto (Knowledge builder)</option>
        </select>
        <select
          aria-label="Type"
          className={input}
          value={documentType}
          onChange={(e) => {
            setDocumentType(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Every type</option>
          {DOCUMENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <button type="button" onClick={openNew} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md">
          Add document
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2 flex justify-between gap-2">
          <span>{error}</span>
          <button type="button" className="text-gray-600 hover:underline shrink-0" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">No documents{origin || documentType ? ' match these filters' : ' yet'}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">Title</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Source</th>
                <th className="py-2 pr-3">Chunks</th>
                <th className="py-2 pr-3">Synced</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const auto = isAutoDocument(d);
                return (
                  <tr key={d.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3">
                      <span className="text-gray-900">{d.title}</span>
                      {auto && (
                        <span
                          className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700"
                          title="Made by the Knowledge builder; re-crawl / re-index replaces it"
                        >
                          ⚙ Auto
                        </span>
                      )}
                      {d.category && <div className="text-xs text-gray-500">{d.category}</div>}
                    </td>
                    <td className="py-2 pr-3 text-gray-700">{d.documentType}</td>
                    <td className="py-2 pr-3 text-gray-600 max-w-[16rem] truncate" title={sourceLabel(d)}>
                      {sourceLabel(d)}
                    </td>
                    <td className="py-2 pr-3">{d.chunkCount ?? '—'}</td>
                    <td className="py-2 pr-3">{d.syncedToVector ? '✓' : '—'}</td>
                    <td className="py-2 whitespace-nowrap space-x-3 text-right">
                      <button type="button" className="text-blue-600 hover:underline" onClick={() => void showChunks(d)}>
                        Chunks
                      </button>
                      <button
                        type="button"
                        className="text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                        disabled={auto}
                        title={auto ? 'Made by the Knowledge builder: change the page and re-crawl instead' : undefined}
                        onClick={() => void openEdit(d)}
                      >
                        Edit
                      </button>
                      <button type="button" className="text-blue-600 hover:underline" disabled={busyId === d.id} onClick={() => void sync(d)}>
                        Sync
                      </button>
                      <button type="button" className="text-red-600 hover:underline" disabled={busyId === d.id} onClick={() => void remove(d)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>{total} document(s)</span>
        <div className="flex items-center gap-2">
          <button type="button" className="px-2 py-1 rounded border disabled:opacity-40" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </button>
          <span>
            {page} / {pages}
          </span>
          <button type="button" className="px-2 py-1 rounded border disabled:opacity-40" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </button>
        </div>
      </div>

      {editor && form && (
        <AiConfigModal title={editor.id == null ? 'Add document' : 'Edit document'} onClose={() => setEditor(null)} maxWidth="lg">
          <input placeholder="Title" value={form.title} onChange={(e) => setForm({ title: e.target.value })} className={`w-full ${input}`} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select
              aria-label="Type"
              value={form.documentType}
              disabled={editor.id != null}
              title={editor.id != null ? 'The type is set when the document is created' : undefined}
              onChange={(e) => setForm({ documentType: e.target.value })}
              className={input}
            >
              {DOCUMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input placeholder="Category (optional)" value={form.category} onChange={(e) => setForm({ category: e.target.value })} className={input} />
            <select aria-label="Language" value={form.language} onChange={(e) => setForm({ language: e.target.value })} className={input}>
              <option value="mn">mn</option>
              <option value="en">en</option>
            </select>
          </div>
          <input placeholder="Tags, comma separated (optional)" value={form.tags} onChange={(e) => setForm({ tags: e.target.value })} className={`w-full ${input}`} />
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-1">
              <input type="radio" checked={form.contentSource === 'text'} onChange={() => setForm({ contentSource: 'text' })} />
              {editor.id == null ? 'Text' : 'Edit the text'}
            </label>
            <label className="flex items-center gap-1">
              <input type="radio" checked={form.contentSource === 'file'} onChange={() => setForm({ contentSource: 'file' })} />
              {editor.id == null ? 'File (PDF / TXT / MD)' : 'Replace with a file'}
            </label>
          </div>
          {form.contentSource === 'text' ? (
            <>
              <textarea placeholder="Text" value={form.content} onChange={(e) => setForm({ content: e.target.value })} className={`w-full ${input}`} rows={10} />
              <CyrillicNote system={system} botId={botId} text={form.content} onFix={(content) => setForm({ content })} />
            </>
          ) : (
            <input type="file" accept=".pdf,.txt,.md,.markdown" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
          )}
          <p className="text-xs text-gray-500">Saving re-splits the document into chunks; the bot uses it after the sync finishes.</p>
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={saving} onClick={() => void save()} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditor(null)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
              Cancel
            </button>
          </div>
        </AiConfigModal>
      )}

      {chunks && (
        <AiConfigModal title={`Chunks — ${chunks.documentTitle}`} onClose={() => setChunks(null)} maxWidth="xl">
          <p className="text-xs text-gray-500">
            {chunks.chunkCount} chunk(s){chunks.chunkCount > chunks.chunks.length ? `, first ${chunks.chunks.length} shown` : ''}.
          </p>
          <ol className="space-y-2 text-sm">
            {chunks.chunks.map((c) => (
              <li key={c.id} className="border rounded p-2">
                <div className="text-xs text-gray-500">
                  #{c.chunkIndex} · {c.tokenCount ?? '?'} tokens · {c.syncedToVector ? 'synced' : 'not synced'}
                </div>
                <div className="whitespace-pre-wrap text-gray-800">{c.content}</div>
              </li>
            ))}
          </ol>
        </AiConfigModal>
      )}
    </section>
  );
}
